import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Input, InputNumber, Spin, notification, Button, Tabs, Table, Checkbox, Modal } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import { Prohibit, HouseLine, Warning, Info, FloppyDisk, X } from '@phosphor-icons/react';
import { roofTextureList } from '../../../../Constants';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import designServices from '../../../../services/oneButtonServices/designServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { RoofProposalDetailModal } from './RoofProposalDetailModal';
import DashboardModal from './DashboardModal';
import { opportunityHasSolarFinancePartner } from './ProductionsCard';
import  quoteServices  from '../../../../services/oneButtonServices/quoteServices';
import { useImperativeHandle } from 'react';


import './oneButton-css/RoofDetailsModal.css';
import {
    type ProductDesignsEmbeddedBridgeProps,
    useProductDesignsEmbeddedFooter,
} from './productDesignsEmbeddedFooter';



const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

export type ShingleCategory = 'cambridge' | 'dynasty';

function getShingleCategory(name: string): ShingleCategory | 'other' {
    const u = String(name || '').toUpperCase();
    if (u.startsWith('IKOCAM') || u === 'IKO4973200') return 'cambridge';
    if (u.startsWith('IKODYN')) return 'dynasty';
    return 'other';
}

/** Matches Angular `normalizeSlopeOverrideValue` / override inputs */
function slopeOverrideToApi(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
}

/** RetrieveSystemDesign Roof: use override fields only — not LowSlope / NormalSlope */
function roofSlopeOverrideFromApi(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function isValidOpportunityId(value: unknown): boolean {
    if (value == null || value === '') return false;
    const text = String(value).trim();
    return text !== '' && text !== 'undefined' && text !== 'null';
}

function pickOpportunityId(...candidates: unknown[]): string | null {
    for (const candidate of candidates) {
        if (isValidOpportunityId(candidate)) return String(candidate).trim();
    }
    return null;
}

export interface RoofDetailsModalProps extends ProductDesignsEmbeddedBridgeProps {
    open: boolean;
    onClose: () => void;
    /** Render body only (no DashboardModal shell) for ProductDesignsModal tabs */
    embedded?: boolean;
    ref?:any
}

export const RoofDetailsModal: React.FC<RoofDetailsModalProps> = ({
    open,
    onClose,
    embedded = false,
    embeddedActive,
    onEmbeddedFooterChange,
    ref
}) => {
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const setPriceQuoteOverrides = useOneButtonStore((s) => s.setPriceQuoteOverrides);
    const theme = useOneButtonStore((s) => s.theme);
    const sceneViewer = useViewerStore((s) => s.sceneViewer);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const storeOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const salesForceId = useOneButtonStore((s) => s.salesForceId);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const setPriceQuoteDetails = useOneButtonStore((s) => s.setPriceQuoteDetails);

    

    /** Angular `roof-detail-ppsf`: `getCapability('UI_Design_Info_Access') == 'View'` disables all `roofForm` controls */
    const designInfoAccessView = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Design_Info_Access') === 'View';

    const [lowSlope, setLowSlope] = useState<number | null>(null);
    const [normalSlope, setNormalSlope] = useState<number | null>(null);
    const [roofDesignDetails, setRoofDesignDetails] = useState<any>(null);
    const [loadingRoofDesign, setLoadingRoofDesign] = useState(false);
    const rrSystemSizeCapability = getOneButtonCapabilityValue(onebuttonCapabilities, 'RR_System_Size');
    const isRRSystemSizeDisabled = designInfoAccessView || rrSystemSizeCapability !== 'Edit';
    const [roofSquareOverride, setRoofSquareOverride] = useState<number | null>(null);
    const [pricePerSqFt, setPricePerSqFt] = useState<number>(0);
    const [pricePerSqFtInput, setPricePerSqFtInput] = useState<string>('0.00');
    const [minSolarRate, setMinSolarRate] = useState<number>(0);
    const [proposedSolarRate, setProposedSolarRate] = useState<number>(0);
    const [loading, setLoading] = useState({ rates: false, saving: false });
    const [roofProposalOpen, setRoofProposalOpen] = useState(false);
    const [pricePerSqFtDisabled, setPricePerSqFtDisabled] = useState(false);
    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 4, stack: false });
    const [submitted, setSubmitted] = useState(false);

    const [shingleCategory, setShingleCategory] = useState<ShingleCategory>('dynasty');
    const [selectedShingle, setSelectedShingle] = useState<string | null>(null);
    const [selectedShingleAlt, setSelectedShingleAlt] = useState<string | null>(null);
    const [selectedPatternObj, setSelectedPatternObj] = useState<any | null>(null);

    const [rrSystemSize, setRrSystemSize] = useState<number | null>(null);
    const [manufacturerList, setManufacturerList] = useState<string[]>([]);
    const [productList, setProductList] = useState<string[]>([]);
    const [manufacturer, setManufacturer] = useState<string | null>(null);
    const [roofProduct, setRoofProduct] = useState<string | null>(null);
    const [dynamicPatterns, setDynamicPatterns] = useState<{ cambridge: any[], dynasty: any[] }>({ cambridge: [], dynasty: [] });

    const handleCategoryChange = (cat: ShingleCategory) => {
        if (designInfoAccessView) return;
        if (cat === 'cambridge' && !canSelectCambridge) return;
        
        setShingleCategory(cat);
        setRoofProduct(cat === 'dynasty' ? 'Dynasty' : 'Cambridge');

        // Reset shingle selection if it doesn't belong to the new category
        if (selectedShingle) {
            const currentCat = getShingleCategory(selectedShingle);
            if (currentCat !== cat && currentCat !== 'other') {
                setSelectedShingle(null);
                setSelectedShingleAlt(null);
                setSelectedPatternObj(null);
            }
        }
    };
    const isInitialLoad = useRef(true);

    // The function you want the parent to trigger
    const updateRoofDetails = async () => {

        const root = opportunityDetails || systemDesign || {};
        const products = (root as any)?.OpportunityProduct || (root as any)?.opportunityProduct || (root as any)?.OpportunityProductsFinanceInfo || [];

        const interest = opportunityDetails?.OpportunityInterest || systemDesign?.OpportunityInterest;
        const roofOnlyProduct = !!(interest?.RoofOnly && !interest?.Roof && !interest?.RRR);
        const roofSource = roofOnlyProduct ? systemDesign?.RoofOnly : systemDesign?.Roof;
        const src =
                roofSource && typeof roofSource === 'object' && !Array.isArray(roofSource)
                    ? (roofSource as Record<string, unknown>)
                    : null;
        const calcTotalBuildingSlope = (designDetails: any, slopeType: 'Low' | 'Normal') => {
            let totalBuildingSlope = 0;
            const buildings = designDetails?.Building;
            if (Array.isArray(buildings) && buildings.length > 0) {
                buildings.forEach((building: any) => {
                    let buildingSlope = 0;
                    const roofs = building?.Roof;
                    if (Array.isArray(roofs) && roofs.length > 0) {
                        roofs.forEach((roofData: any) => {
                            if (roofData?.Active_Ind === true) {
                                buildingSlope += Number(roofData?.[`${slopeType}_Square`] ?? 0);
                            }
                        });
                    }
                    totalBuildingSlope += designServices.roundedBasedOn33Percent(buildingSlope);
                });
            }
            return totalBuildingSlope;
        };
        
        const calcRegularRoofSquares = (designDetails: any) => {
            const LowSlope = calcTotalBuildingSlope(designDetails, 'Low');
            const NormalSlope = calcTotalBuildingSlope(designDetails, 'Normal');
            const totalRoofSquares = (LowSlope ? LowSlope : 0) + (NormalSlope ? NormalSlope : 0);
            return { LowSlope, NormalSlope, totalRoofSquares };
        };
        let designForSave: any = null;

        const sfIdForSave = opportunityDetails?.SalesForce_Id || systemDesign?.SalesForce_Id;
        const sourceForSave = String(systemDesign?.Design_Source || (opportunityDetails as any)?.Design_Source || '').toLowerCase();
        if (sfIdForSave) {
            try {
                designForSave =
                    sourceForSave === 'onedraw'
                        ? await designServices.RetrieveBuildingWithRoof(String(sfIdForSave))
                        : await designServices.GetRoofAreasByOpportunityId(String(sfIdForSave));
            } catch {
                designForSave = null;
            }
        }
        const { LowSlope: designLow, NormalSlope: designNormal, totalRoofSquares: designRoofSquare } =
                    calcRegularRoofSquares(designForSave);
        const lowOv = slopeOverrideToApi(lowSlope);
        const normalOv = slopeOverrideToApi(normalSlope);
        const roofSquareOverrideSum = (lowOv ?? 0) + (normalOv ?? 0);
                

        const roofPayload: Record<string, unknown> = {
            ...(src || {}),
            Manufacturer: manufacturer,
            RoofProduct: roofProduct,
            LowSlope: designLow,
            NormalSlope: designNormal,
            RoofSquare: designRoofSquare,
            Low_Slope_Override: lowOv,
            Normal_Slope_Override: normalOv,
            RoofSquareOverride: roofSquareOverrideSum,
            PricePerSqft: pricePerSqFt,
            ProductNumber: selectedShingle,
            ShingleColor: selectedShingleAlt,
            SupplierName: selectedPatternObj?.supplierName || (src as any)?.SupplierName || '',
            PrimaryColor: selectedPatternObj?.primaryColor || (src as any)?.PrimaryColor || '',
            ShingleColorSF: selectedPatternObj?.salesforceListing || (src as any)?.ShingleColorSF || '',
            MinRate: minSolarRate,
            ProposedRate: proposedSolarRate,
            RR_System_Size: rrSystemSize
        };



        const sd = systemDesign;

        if(roofOnlyProduct) setSystemDesign({ ...sd, RoofOnly:roofPayload });
        else setSystemDesign({ ...sd, Roof:roofPayload });

    };

    // Expose the function to the parent ref
    useImperativeHandle(ref, () => ({
        updateRoofDetails: updateRoofDetails
    }));

    const actualRoofSquares = useMemo(() => designServices.getRoofSquaresFromSystemDesign(systemDesign), [systemDesign]);
    const activeRoofData = useMemo(() => {
        if (!systemDesign) return null;
        const interest: any = systemDesign.OpportunityInterest;
        if (interest?.Roof || interest?.RRR) return systemDesign.Roof;
        if (interest?.RoofOnly) return systemDesign.RoofOnly;
        return null;
    }, [systemDesign]);

    const canSelectCambridge = useMemo(() => {
        if (!activeRoofData) return false;
        const roofs = Array.isArray(activeRoofData) ? activeRoofData : [activeRoofData];
        for (const r of roofs) {
            const existingShingle = (r as any).ProductNumber ? String((r as any).ProductNumber) : null;
            if (existingShingle && getShingleCategory(existingShingle) === 'cambridge') {
                return true;
            }
        }
        return false;
    }, [activeRoofData]);

    const calculatedSlopes = useMemo(() => {
        let lowSum = 0;
        let normalSum = 0;
        
        if (roofDesignDetails?.Building && Array.isArray(roofDesignDetails.Building)) {
            const isNewCalculationLogic = systemDesign?.ApplyRoofWastage === true;
            const isActive = (v: any) => {
                if (v === false || v === 0 || v === '0' || String(v).toLowerCase() === 'false') return false;
                return !!v;
            };

            roofDesignDetails.Building.forEach((b: any) => {
                const roofData = b.Roof || [];
                
                const buildingLowTotal = designServices.roundedBasedOn33Percent(
                    roofData.reduce((acc: number, r: any) => acc + (isActive(r.Active_Ind) ? (Number(r.Low_Square) || 0) : 0), 0),
                    isNewCalculationLogic
                );
                
                const buildingNormalTotal = designServices.roundedBasedOn33Percent(
                    roofData.reduce((acc: number, r: any) => acc + (isActive(r.Active_Ind) ? (Number(r.Normal_Square) || 0) : 0), 0),
                    isNewCalculationLogic
                );
                
                lowSum += buildingLowTotal;
                normalSum += buildingNormalTotal;
            });
        }
        
        return { lowSum, normalSum, totalSum: lowSum + normalSum };
    }, [roofDesignDetails, activeRoofData, systemDesign?.ApplyRoofWastage]);

    const isSlopeDataMissing = useMemo(() => {
        return calculatedSlopes.totalSum <= 0;
    }, [calculatedSlopes]);

    useEffect(() => {
        const fetchBuildingDetails = async () => {
            const sfId =
                opportunityDetails?.SalesForce_Id ||
                systemDesign?.SalesForce_Id ||
                salesForceId ||
                undefined;
            const source = String(systemDesign?.Design_Source || (opportunityDetails as any)?.Design_Source || '').toLowerCase();
            
            if (sfId) {
                setLoadingRoofDesign(true);
                try {
                    let data = source === 'onedraw'
                            ? await designServices.RetrieveBuildingWithRoof(String(sfId))
                            : await designServices.GetRoofAreasByOpportunityId(String(sfId));
                    if (typeof data === 'string') {
                        try {
                            data = JSON.parse(data);
                        } catch (e) {
                            console.error("Failed to parse building data", e);
                        }
                    }
                    setRoofDesignDetails(data);
                } catch {
                    setRoofDesignDetails(null);
                } finally {
                    setLoadingRoofDesign(false);
                }
            }
        };

        if (open) {
            fetchBuildingDetails();
        }
    }, [
        open, 
        opportunityDetails?.SalesForce_Id, 
        systemDesign?.SalesForce_Id,
        salesForceId,
        systemDesign?.Design_Source
    ]);

    const buildingTabs = useMemo(() => {
        if (!roofDesignDetails?.Building || !Array.isArray(roofDesignDetails.Building)) return null;

        const isNewCalculationLogic = systemDesign?.ApplyRoofWastage === true;

        const items = roofDesignDetails.Building.map((b: any) => {
            const roofData = b.Roof || [];
            
            const columns = [
                {
                    title: 'Active',
                    dataIndex: 'Active_Ind',
                    key: 'active',
                    render: (val: any) => {
                        const isChecked = !(val === false || val === 0 || val === '0' || String(val).toLowerCase() === 'false') && !!val;
                        return <Checkbox checked={isChecked} disabled />;
                    }
                },
                {
                    title: 'Roof #',
                    dataIndex: 'Name',
                    key: 'roofId'
                },
                {
                    title: 'Low',
                    dataIndex: 'Low_Square',
                    key: 'low',
                    render: (val: any) => Number(val || 0).toFixed(2)
                },
                {
                    title: 'Normal',
                    dataIndex: 'Normal_Square',
                    key: 'normal',
                    render: (val: any) => Number(val || 0).toFixed(2)
                },
                {
                    title: 'Total',
                    key: 'total',
                    align: 'center' as const,
                    render: (_: any, __: any, index: number) => ({
                        children: '',
                        props: { rowSpan: index === 0 ? roofData.length : 0 }
                    })
                }
            ];

            const isActive = (v: any) => {
                if (v === false || v === 0 || v === '0' || String(v).toLowerCase() === 'false') return false;
                return !!v;
            };

            const rawLowTotal = roofData.reduce((acc: number, r: any) => acc + (isActive(r.Active_Ind) ? (Number(r.Low_Square) || 0) : 0), 0);
            const rawNormalTotal = roofData.reduce((acc: number, r: any) => acc + (isActive(r.Active_Ind) ? (Number(r.Normal_Square) || 0) : 0), 0);

            const lowTotal = isNewCalculationLogic
                ? Number(rawLowTotal.toFixed(2))
                : designServices.roundedBasedOn33Percent(rawLowTotal, isNewCalculationLogic);

            const normalTotal = isNewCalculationLogic
                ? Number(rawNormalTotal.toFixed(2))
                : designServices.roundedBasedOn33Percent(rawNormalTotal, isNewCalculationLogic);

            return {
                label: b.Building_Name,
                key: b.Building_Id,
                children: (
                    <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                        <Table
                            className="obd-roof-modal__building-table"
                            dataSource={roofData}
                            columns={columns}
                            pagination={false}
                            size="small"
                            rowKey="Roof_Id"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2}>
                                        Building Total
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                        {lowTotal}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                        {normalTotal}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        {lowTotal + normalTotal}
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </div>
                )
            };
        });

        return <Tabs items={items} />;
    }, [roofDesignDetails, systemDesign?.ApplyRoofWastage]);

    // Angular parity: show error when exactly one override value is provided
    const hasOneOverrideOnly = useMemo(() => {
        const lowOv = slopeOverrideToApi(lowSlope);
        const normalOv = slopeOverrideToApi(normalSlope);
        return (lowOv !== null && normalOv === null) || (lowOv === null && normalOv !== null);
    }, [lowSlope, normalSlope]);

    const showMinRateandPPR = useMemo(() => {
        const interest = systemDesign?.OpportunityInterest || opportunityDetails?.OpportunityInterest;
        if (interest?.Solar) return true;

        const products = opportunityDetails?.OpportunityProduct || [];
        return products.some((item: any) => item.Product_Type_Name === 'Solar');
    }, [opportunityDetails, systemDesign]);

    const shinglesByCategory = useMemo(() => {
        const categories = { 
            cambridge: dynamicPatterns.cambridge.length > 0 ? dynamicPatterns.cambridge : [] as any[], 
            dynasty: dynamicPatterns.dynasty.length > 0 ? dynamicPatterns.dynasty : [] as any[], 
            other: [] as any[] 
        };
        
        // If we don't have dynamic patterns yet, fallback to static list for safety but we aim for dynamic
        if (categories.cambridge.length === 0 && categories.dynasty.length === 0) {
            roofTextureList.forEach((item) => {
                const cat = getShingleCategory(item.name);
                if (cat in categories) categories[cat as keyof typeof categories].push(item);
                else categories.other.push(item);
            });
        }
        return categories;
    }, [dynamicPatterns]);

    useEffect(() => {
        if (!open) return;
        
        // 1. Start with values from systemDesign
        const roofData = activeRoofData;
        let baseLow: number | null = null;
        let baseNormal: number | null = null;
        let baseSq = null;
        let basePpsf = 0;
        let baseMin = 0;
        let baseProp = 0;
        let baseShingle = null;
        let baseAlt = null;
        let baseManufacturer = null;
        let baseProduct = null;
        let baseRRSize = null;

        if (roofData && !Array.isArray(roofData)) {
            const r = roofData as any;
            baseLow = roofSlopeOverrideFromApi(r.Low_Slope_Override);
            baseNormal = roofSlopeOverrideFromApi(r.Normal_Slope_Override);
            baseSq = r.RoofSquareOverride != null ? Number(r.RoofSquareOverride) : null;
            basePpsf = Number(r.PricePerSqft ?? 0);
            baseMin = Number(r.MinRate ?? 0);
            baseProp = Number(r.ProposedRate ?? 0);
            baseShingle = r.ProductNumber ? String(r.ProductNumber) : null;
            baseAlt = r.ShingleColor ?? null;
            baseManufacturer = r.Manufacturer || null;
            baseProduct = r.RoofProduct || null;
            baseRRSize = r.RR_System_Size != null ? Number(r.RR_System_Size) : null;
        }

        // 2. Store overrides win when defined; otherwise API override-only values (null → blank inputs)
        setLowSlope(
            priceQuoteOverrides?.lowSlopeOverride !== undefined
                ? priceQuoteOverrides.lowSlopeOverride
                : baseLow
        );
        setNormalSlope(
            priceQuoteOverrides?.normalSlopeOverride !== undefined
                ? priceQuoteOverrides.normalSlopeOverride
                : baseNormal
        );
        setRoofSquareOverride(priceQuoteOverrides?.roofSquareOverride != null ? Number(priceQuoteOverrides.roofSquareOverride) : baseSq);
        const nextPricePerSqFt = Number(priceQuoteOverrides?.pricePerSqFt ?? basePpsf);
        setPricePerSqFt(nextPricePerSqFt);
        setPricePerSqFtInput(nextPricePerSqFt.toFixed(2));
        setRrSystemSize(priceQuoteOverrides?.rrSystemSize != null ? Number(priceQuoteOverrides.rrSystemSize) : baseRRSize);
        setManufacturer(baseManufacturer);
        setRoofProduct(baseProduct);
        
        const shingleId = priceQuoteOverrides?.selectedShingleId ?? baseShingle;
        const effectivePpsf = Number(priceQuoteOverrides?.pricePerSqFt ?? basePpsf);

        if (shingleId) {
            let cat = getShingleCategory(String(shingleId));
            
            setSelectedShingle(String(shingleId));
            const texture = roofTextureList.find(t => t.name === shingleId);
            setSelectedShingleAlt(priceQuoteOverrides?.selectedShingleAlt ?? texture?.alt ?? baseAlt);
            if (cat === 'cambridge' || cat === 'dynasty') {
                setShingleCategory(cat);
            }
        } else {
            setShingleCategory('dynasty');
            if (!baseProduct) {
                setRoofProduct('Dynasty');
            }
        }
        setMinSolarRate(Number(priceQuoteOverrides?.minSolarRate ?? baseMin));
        setProposedSolarRate(Number(priceQuoteOverrides?.proposedSolarRate ?? baseProp));
    }, [open, systemDesign, priceQuoteOverrides, activeRoofData]);

    // Fetch Manufacturers and Products on load/change
    useEffect(() => {
        if (!open) return;
        
        const fetchMasterData = async () => {
            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const leadSource = opportunityDetails?.Sddf_lead_source || '';
            const salesDivision = opportunityDetails?.Sales_Division || '';
            const interest = systemDesign?.OpportunityInterest || opportunityDetails?.OpportunityInterest;
            const pType = interest?.Roof ? 'R' : (interest?.Solar ? 'Solar' : 'Roof'); // Logical mapping from Angular

            // 1. Get Manufacturers
            const manufPayload = {
                ExcelFileName: "Roofing Calculator",
                SheetName: "Roof Pricing",
                InputValues: [
                    { CellNumber: "C3", CellValue: stateId, Type: "string" },
                    { CellNumber: "C4", CellValue: pType, Type: "string" },
                    { CellNumber: "C5", CellValue: interest?.Solar ? 'Solar' : 'Roofing', Type: "string" },
                    { CellNumber: "H6", CellValue: leadSource, Type: "string" },
                    { CellNumber: "H7", CellValue: salesDivision, Type: "string" }
                ],
                OutputCells: ["E3"]
            };
            
            try {
                const res = await masterServices.cascadingDropDown(manufPayload);
                const list = (res?.[0]?.CellValue ? String(res[0].CellValue).split('|') : []).map(s => s.trim()).filter(Boolean);
                setManufacturerList(list);
                if (!manufacturer && list.length > 0) setManufacturer(list[0]);
            } catch (err) { console.error("Error fetching manufacturers", err); }
        };

        fetchMasterData();
    }, [open, opportunityDetails, systemDesign, manufacturer]);

    useEffect(() => {
        if (!open || !manufacturer) return;

        const fetchProducts = async () => {
            const stateId = opportunityDetails?.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
            const leadSource = opportunityDetails?.Sddf_lead_source || '';
            const salesDivision = opportunityDetails?.Sales_Division || '';
            const interest = systemDesign?.OpportunityInterest || opportunityDetails?.OpportunityInterest;
            const pType = interest?.Roof ? 'Roof' : 'R';

            const prodPayload = {
                ExcelFileName: "Shingle Calculator",
                SheetName: "Shingle Calculator",
                InputValues: [
                    { CellNumber: "C3", CellValue: stateId, Type: "string" },
                    { CellNumber: "C4", CellValue: pType, Type: "string" },
                    { CellNumber: "C5", CellValue: manufacturer, Type: "string" },
                    { CellNumber: "H6", CellValue: leadSource, Type: "string" },
                    { CellNumber: "H7", CellValue: salesDivision, Type: "string" }
                ],
                OutputCells: ["F7"]
            };

            try {
                const res = await masterServices.cascadingDropDown(prodPayload);
                const list = (res?.[0]?.CellValue ? String(res[0].CellValue).split('|') : []).map(s => s.trim()).filter(Boolean);
                setProductList(list);
                if (!roofProduct && list.length > 0) setRoofProduct(list[0]);
            } catch (err) { console.error("Error fetching products", err); }
        };

        fetchProducts();
    }, [open, manufacturer, opportunityDetails, systemDesign, roofProduct]);

    useEffect(() => {
        if (open && opportunityDetails?.Opportunity_Id) {
            designServices.getRoofcontractsigned(opportunityDetails.Opportunity_Id).then(setPricePerSqFtDisabled);
        }
    }, [open, opportunityDetails?.Opportunity_Id]);

    useEffect(() => {
        if (open && pricePerSqFt > 0) {
            updateMinandProposedRate(pricePerSqFt);
        }
    }, [open]);

    const updateMinandProposedRate = async (ppsf: number) => {
        if (!opportunityDetails) return;
        setLoading(prev => ({ ...prev, rates: true }));
        const stateId = opportunityDetails.InstallationAddress?.stateCode || systemDesign?.InstallationAddress?.StateId || '';
        const utilityCompanyName = systemDesign?.SolarUtility?.UtilityCompanyName || '';
        const products = opportunityDetails.OpportunityProduct || [];

        let financePartnerName = null;
        let financeMethod = 'Loan';

        const matchedProduct = products.find((item: any) =>
            item.Product_Type_Name === 'Roof' &&
            (item.Finance_Method_Name?.toLowerCase().includes('cash') || item.Finance_Method_Name?.toLowerCase().includes('loan'))
        );

        if (matchedProduct) {
            const methodName = matchedProduct.Finance_Method_Name?.toLowerCase();
            financeMethod = methodName.includes('cash') ? 'Cash' : 'Loan';
            financePartnerName = matchedProduct.Finance_Partner_Display_Name || null;
        } else {
            const hasRoofCash = products.some((item: any) =>
                item.Product_Type_Name === 'Roof' && item.Finance_Partner_Display_Name === 'Cash'
            );
            financeMethod = hasRoofCash ? 'Cash' : 'Loan';
        }

        const payload = {
            ExcelFileName: 'Shingle Calculator',
            SheetName: 'shingleCalculatorV2',
            InputValues: [{ Type: 'String', CellNumber: 'C3', CellValue: { State: stateId, Utility: utilityCompanyName, financePartner: financePartnerName, financeMethod, pricePerSqft: ppsf } }],
            OutputCells: ['F3'],
        };

        try {
            const res = await masterServices.cascadingDropDown(payload);
            const response = res?.[0]?.CellValue ? JSON.parse(res[0].CellValue) : null;
            const pricing = response?.pricingDetails;
            setMinSolarRate(Number(pricing?.minimumRate || 0));
            setProposedSolarRate(Number(pricing?.proposedRate || 0));

            // Extract dynamic patterns
            if (response) {
                const mapShingles = (list: any[]) => (list || []).map(item => {
                    const matched = roofTextureList.find(t => t.name === item.shingleProductCode);
                    return {
                        name: item.shingleProductCode,
                        alt: item.shingleColor || item.salesforceListing || '',
                        id: item.shingleProductCode,
                        img: matched ? matched.img : `./assets/images/patterns/${item.shingleProductCode}.jpg`,
                        supplierName: item.supplierName,
                        primaryColor: item.primaryColor,
                        salesforceListing: item.salesforceListing
                    };
                });

                const cambridge = mapShingles(response.ikoCambridgeColors);
                const dynasty = mapShingles(response.ikoDynastyColors);

                setDynamicPatterns({
                    cambridge,
                    dynasty
                });

                // Sync selectedPatternObj if already selected
                if (selectedShingle) {
                    const all = [...cambridge, ...dynasty];
                    const found = all.find(p => p.id === selectedShingle || p.name === selectedShingle);
                    if (found) setSelectedPatternObj(found);
                }
            }
        } catch (err) {
            console.error('Error updating solar rates and patterns:', err);
            setMinSolarRate(0);
            setProposedSolarRate(0);
        } finally {
            setLoading(prev => ({ ...prev, rates: false }));
        }
    };

    const handleSave = async () => {
        if (designInfoAccessView) return;
        setSubmitted(true);
        const interest = opportunityDetails?.OpportunityInterest || systemDesign?.OpportunityInterest;
        if (interest?.RRR && (rrSystemSize === null || rrSystemSize === 0 || String(rrSystemSize) === '')) {
            noticeAPI.warning({
                message: 'Selection Required',
                description: 'Please enter R&R system size in Kilowatts.',
                placement: 'topRight',
                className: `obd-notification obd-notification-warning obd-theme-${theme}`
            });
            return;
        }
        const productName = (interest?.Roof || interest?.RoofOnly) ? 'Roof' : 'RRR';
        if (!selectedShingle) {
            noticeAPI.warning({
                message: 'Selection Required',
                description: `Please choose a Shingle Color to finalize and Save the ${productName} Design.`,
                placement: 'topRight',
                className: `obd-notification obd-notification-warning obd-theme-${theme}`
            });
            return;
        }

        if (Number(pricePerSqFt) <= 0) {
            noticeAPI.warning({
                message: 'Validation Error',
                description: 'Please enter a valid Price / Sq Ft before saving.',
                placement: 'topRight',
                className: `obd-notification obd-notification-warning obd-theme-${theme}`
            });
            return;
        }

        // Design Total (Low+Normal) is 0/unavailable → user must enter override values.
        if (isSlopeDataMissing) {
            const lowOv = slopeOverrideToApi(lowSlope);
            const normalOv = slopeOverrideToApi(normalSlope);
            const overrideSum = (lowOv ?? 0) + (normalOv ?? 0);
            if (lowOv === null && normalOv === null) {
                Modal.warning({
                    title: 'Slope override required',
                    content:
                        'Slope data for this site is not available. Enter Low and Normal override values greater than 0 to save.',
                    okText: 'OK',
                    centered: true,
                    zIndex: 14000,
                    className: `obd-theme-${theme}`,
                    rootClassName: `obd-theme-${theme}`,
                });
                return;
            }
            if (hasOneOverrideOnly || overrideSum <= 0) {
                Modal.warning({
                    title: 'Slope override required',
                    content:
                        'Enter both Low and Normal override values. The total must be greater than 0 when design slope totals are unavailable.',
                    okText: 'OK',
                    centered: true,
                    zIndex: 14000,
                    className: `obd-theme-${theme}`,
                    rootClassName: `obd-theme-${theme}`,
                });
                return;
            }
        }

        const resolvedOpportunityId = pickOpportunityId(
            opportunityDetails?.Opportunity_Id,
            opportunityDetails?.OpportunityId,
            systemDesign?.Opportunity_Id,
            systemDesign?.OpportunityId,
            storeOpportunityId,
        );
        if (!resolvedOpportunityId) {
            noticeAPI.warning({
                message: 'Opportunity not loaded',
                description: 'The opportunity id is not available yet. Wait a moment and try again.',
                placement: 'topRight',
                className: `obd-notification obd-notification-warning obd-theme-${theme}`
            });
            return;
        }

        setLoading(prev => ({ ...prev, saving: true }));
        try {
            const root = opportunityDetails || systemDesign || {};
            const products = (root as any)?.OpportunityProduct || (root as any)?.opportunityProduct || (root as any)?.OpportunityProductsFinanceInfo || [];
            const solarRow = products.find((p: any) => (p.Product_Type_Name === 'Solar' || p.product_Type_Name === 'Solar') && p.ActiveFlag);

            const interest = opportunityDetails?.OpportunityInterest || systemDesign?.OpportunityInterest;
            const roofOnlyProduct = !!(interest?.RoofOnly && !interest?.Roof && !interest?.RRR);
            const roofSource = roofOnlyProduct ? systemDesign?.RoofOnly : systemDesign?.Roof;
            const src =
                roofSource && typeof roofSource === 'object' && !Array.isArray(roofSource)
                    ? (roofSource as Record<string, unknown>)
                    : null;

            const sfIdForSave = opportunityDetails?.SalesForce_Id || systemDesign?.SalesForce_Id;
            const sourceForSave = String(systemDesign?.Design_Source || (opportunityDetails as any)?.Design_Source || '').toLowerCase();
            let designForSave: any = null;
            if (sfIdForSave) {
                try {
                    designForSave =
                        sourceForSave === 'onedraw'
                            ? await designServices.RetrieveBuildingWithRoof(sfIdForSave)
                            : await designServices.GetRoofAreasByOpportunityId(sfIdForSave);
                } catch {
                    designForSave = null;
                }
            }
            const isNewCalculationLogic = systemDesign?.ApplyRoofWastage === true;
            
            const calcTotalBuildingSlope = (designDetails: any, slopeType: 'Low' | 'Normal') => {
                let totalBuildingSlope = 0;
                const buildings = designDetails?.Building;
                if (Array.isArray(buildings) && buildings.length > 0) {
                    buildings.forEach((building: any) => {
                        let buildingSlope = 0;
                        const roofs = building?.Roof;
                        if (Array.isArray(roofs) && roofs.length > 0) {
                            roofs.forEach((roofData: any) => {
                                if (roofData?.Active_Ind === true) {
                                    buildingSlope += Number(roofData?.[`${slopeType}_Square`] ?? 0);
                                }
                            });
                        }
                        totalBuildingSlope += designServices.roundedBasedOn33Percent(buildingSlope, isNewCalculationLogic);
                    });
                }
                return totalBuildingSlope;
            };
            const calcRegularRoofSquares = (designDetails: any) => {
                const LowSlope = calcTotalBuildingSlope(designDetails, 'Low');
                const NormalSlope = calcTotalBuildingSlope(designDetails, 'Normal');
                const totalRoofSquares = (LowSlope ? LowSlope : 0) + (NormalSlope ? NormalSlope : 0);
                return { LowSlope, NormalSlope, totalRoofSquares };
            };
            const { LowSlope: designLow, NormalSlope: designNormal, totalRoofSquares: designRoofSquare } =
                calcRegularRoofSquares(designForSave);
            const lowOv = slopeOverrideToApi(lowSlope);
            const normalOv = slopeOverrideToApi(normalSlope);
            const roofSquareOverrideSum = (lowOv ?? 0) + (normalOv ?? 0);

            const roofPayload: Record<string, unknown> = {
                ...(src || {}),
                Manufacturer: manufacturer,
                RoofProduct: roofProduct,
                Wastage: isNewCalculationLogic ? 0 : 15,
                LowSlope: designLow,
                NormalSlope: designNormal,
                RoofSquare: designRoofSquare,
                Low_Slope_Override: lowOv,
                Normal_Slope_Override: normalOv,
                RoofSquareOverride: roofSquareOverrideSum,
                PricePerSqft: pricePerSqFt,
                ProductNumber: selectedShingle,
                ShingleColor: selectedShingleAlt,
                SupplierName: selectedPatternObj?.supplierName || (src as any)?.SupplierName || '',
                PrimaryColor: selectedPatternObj?.primaryColor || (src as any)?.PrimaryColor || '',
                ShingleColorSF: selectedPatternObj?.salesforceListing || (src as any)?.ShingleColorSF || '',
                MinRate: minSolarRate,
                ProposedRate: proposedSolarRate,
                RR_System_Size: rrSystemSize
            };

            const payload: any = {
                Opportunity_Id: resolvedOpportunityId,
                OpportunityInterest: opportunityDetails?.OpportunityInterest || systemDesign?.OpportunityInterest,
                SalesForce_Id:
                    opportunityDetails?.SalesForce_Id ||
                    systemDesign?.SalesForce_Id ||
                    salesForceId ||
                    undefined,
                SolarProductId: Number(systemDesign?.SolarProductId || 0),
                BatteryProductId: Number(systemDesign?.BatteryProductId || 0),
                BatteryOnlyProductId: Number(systemDesign?.BatteryOnlyProductId || 0),
                RoofProductId: Number(systemDesign?.RoofProductId || 0),
                RoofOnlyProductId: Number(systemDesign?.RoofOnlyProductId || 0),
                SolarUtility: systemDesign?.SolarUtility,
                BatteryInformation: systemDesign?.BatteryInformation,
                Roof: roofOnlyProduct ? systemDesign?.Roof : roofPayload,
                RoofOnly: roofOnlyProduct ? roofPayload : systemDesign?.RoofOnly,
                BatteryOnly: systemDesign?.BatteryOnly,
                Multiplier: systemDesign?.Multiplier ?? null,
                SystemSize: systemDesign?.SystemSize ?? null,
                SystemSizeDouble: Number(systemDesign?.SystemSizeDouble || 0),
                Production: systemDesign?.Production ?? null,
                ProductionDouble: Number(systemDesign?.ProductionDouble || 0),
                IsDomestic: systemDesign?.IsDomestic ?? false,
                FinanceMethod: solarRow?.Finance_Method_Name || systemDesign?.FinanceMethod || '',
                designJson: systemDesign?.designJson || (systemDesign?.DesignArray ? JSON.stringify(systemDesign.DesignArray) : '[]'),
                isManual: systemDesign?.isManual ?? false
            };
            await designServices.SaveSystemDesign(payload);




            const refresh = await designServices.RetrieveSystemDesign(payload.Opportunity_Id);
            if (refresh) {
                setSystemDesign(refresh);
                handleSaveOverrides();
            }
            
            noticeAPI.success({
                message: 'Success',
                description: 'Roof details updated successfully.',
                placement: 'topRight',
                className: `obd-notification obd-notification-success obd-theme-${theme}`
            });

            const priceQuote = await quoteServices.getRetrievePriceQuote(String(payload.Opportunity_Id ?? ''));
            setPriceQuoteDetails(priceQuote as any);

            setTimeout(onClose, 800);
        } catch (e) {
            console.error("Save Error", e);
            noticeAPI.error({
                message: 'Error',
                description: 'Failed to save Roof Information. Please try again.',
                placement: 'topRight',
                className: `obd-notification obd-notification-error obd-theme-${theme}`
            });
        } finally {
            setLoading(prev => ({ ...prev, saving: false }));
        }
    };

    const getNormalizationCode = (type: string) => {
        const t = (type || '').toLowerCase();
        if (t.includes(SolarProductName.toLowerCase())) return 'S';
        if (t.includes(RoofProductName.toLowerCase())) return 'R';
        if (t.includes(BatteryProductName.toLowerCase())) return 'B';
        if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
        return 'S'; // Default to Solar if unknown
    };

    const handleSaveOverrides = () => {
        const next: NonNullable<typeof priceQuoteOverrides> = {
            ...priceQuoteOverrides,
            roofSquareOverride: roofSquareOverride ?? priceQuoteOverrides?.roofSquareOverride ?? null,
            normalSlopeOverride: normalSlope ?? null,
            lowSlopeOverride: lowSlope ?? null,
            pricePerSqFt,
            selectedShingleId: selectedShingle ?? priceQuoteOverrides?.selectedShingleId ?? null,
            selectedShingleAlt: selectedShingleAlt ?? priceQuoteOverrides?.selectedShingleAlt ?? null,
            minSolarRate: minSolarRate || priceQuoteOverrides?.minSolarRate || null,
            proposedSolarRate: proposedSolarRate || priceQuoteOverrides?.proposedSolarRate || null,
            rrSystemSize,
        };
        setPriceQuoteOverrides(next);
    };

    const handleShingleSelect = (textureId: string, alt: string, imgSrc: string) => {
        setSelectedShingle(textureId);
        setSelectedShingleAlt(alt);
        
        // Find the full pattern object for save payload
        const all = [...dynamicPatterns.cambridge, ...dynamicPatterns.dynasty];
        const found = all.find(p => p.id === textureId || p.name === textureId);
        if (found) setSelectedPatternObj(found);

        if (sceneViewer?.roofMeshArr?.length && imgSrc) {
            sceneViewer.setMeshTexture(sceneViewer.roofMeshArr, imgSrc, 'map', textureId);
        }
    };


    const renderShingleGrid = (category: ShingleCategory) => {
        const list = category === 'cambridge' ? shinglesByCategory.cambridge : shinglesByCategory.dynasty;
        const selectionBlocked = designInfoAccessView || (category === 'cambridge' && !canSelectCambridge);
        
        return (
            <div className="obd-roof-modal__shingle-grid">
                {list.map((item: any) => {
                    const src = (item.img as string) || (item.img as { default?: string })?.default || '';
                    const isSelected = selectedShingle === item.name || selectedShingle === item.id;
                    const name = item.alt || item.scolor || '';
                    const code = item.name || item.id || '';
                    return (
                        <button
                            key={item.id || item.name}
                            type="button"
                            className={`obd-roof-modal__shingle-tile ${isSelected ? 'obd-roof-modal__shingle-tile--selected' : ''} ${selectionBlocked ? 'obd-roof-modal__shingle-tile--view-only' : ''}`}
                            onClick={() => !selectionBlocked && handleShingleSelect(item.name || item.id, name, src)}
                            title={!selectionBlocked ? `${name} (${code})` : `Locked - Preview Only`}
                            style={{ cursor: !selectionBlocked ? 'pointer' : 'default' }}
                        >
                            <span className="obd-roof-modal__shingle-img-wrap">
                                <img src={src || undefined} alt={name} className="obd-roof-modal__shingle-img" />
                                {selectionBlocked && (
                                    <div className="obd-roof-modal__shingle-lock-badge">
                                        <Prohibit size={14} weight="bold" />
                                    </div>
                                )}
                            </span>
                            <span className="obd-roof-modal__shingle-name">{name}</span>
                            <span className="obd-roof-modal__shingle-code">{code}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    const roofFooterPrimary = (
        <>
            <Button icon={<X size={14} weight="bold" />} onClick={onClose} disabled={loading.saving}>
                Cancel
            </Button>
            <Button
                type="primary"
                icon={<FloppyDisk size={14} weight="bold" />}
                onClick={handleSave}
                disabled={loading.saving || designInfoAccessView}
                loading={loading.saving}
                title={
                    isSlopeDataMissing &&
                    ((slopeOverrideToApi(lowSlope) ?? 0) + (slopeOverrideToApi(normalSlope) ?? 0) <= 0 ||
                        hasOneOverrideOnly)
                        ? 'Enter Low and Normal slope overrides greater than 0 to save'
                        : undefined
                }
            >
                Save
            </Button>
        </>
    );

    const roofModalFooter = embedded ? (
        <div className="obd-product-designs-modal__footer-bar">
            <div className="obd-product-designs-modal__footer-start">
                <Button
                    icon={<HouseLine size={14} weight="regular" />}
                    onClick={() => setRoofProposalOpen(true)}
                    title="Open Roof Proposal View"
                >
                    Proposal
                </Button>
            </div>
            <div className="obd-product-designs-modal__footer-end">{roofFooterPrimary}</div>
        </div>
    ) : (
        <div className="obd-roof-modal__actions obd-roof-modal__actions--split">
            <Button
                icon={<HouseLine size={14} weight="regular" />}
                onClick={() => setRoofProposalOpen(true)}
                title="Open Roof Proposal View"
            >
                Proposal
            </Button>
            <div className="obd-roof-modal__actions-primary">{roofFooterPrimary}</div>
        </div>
    );

    useProductDesignsEmbeddedFooter(
        embedded,
        embeddedActive,
        open,
        roofModalFooter,
        onEmbeddedFooterChange,
    );

    const roofModalBody = (
        <>

            {noticeContext}
            <div className="obd-roof-modal" style={{ position: 'relative' }}>
                <div className="obd-roof-modal__body">
                    <div className="obd-roof-modal__grid-container">
                        {/* Sidebar (Left) — slope configuration only */}
                        <aside className="obd-roof-modal__sidebar obd-roof-modal__sidebar--slope-only">
                            <div className={`obd-roof-modal__config-card obd-roof-modal__config-card--slope ${isSlopeDataMissing ? 'obd-roof-modal__config-card--alert' : ''}`}>
                                <div className="obd-roof-modal__card-header">
                                    <h3 className="obd-roof-modal__section-title">Slope Configuration</h3>
                                </div>
                                {isSlopeDataMissing && (
                                    <div className="obd-roof-modal__config-alert-banner">
                                        <Warning size={18} weight="fill" />
                                        <span>Slope data for this site is not available, Please enter override value to proceed</span>
                                    </div>
                                )}
                                <div className="obd-roof-modal__sidebar-matrix">
                                    {loadingRoofDesign ? <Spin /> : buildingTabs}
                                    <table className="obd-roof-modal__matrix-table">
                                        <thead>
                                            <tr>
                                                <th>Slope</th>
                                                <th>Total</th>
                                                <th>Override</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Low</td>
                                                <td className="obd-roof-modal__matrix-val-dim">
                                                    {calculatedSlopes.lowSum}
                                                </td>
                                                <td>
                                                    <InputNumber 
                                                        min={0}
                                                        disabled={designInfoAccessView}
                                                        onKeyDown={(e) => {
                                                            if (e.key === '.' || e.key === ',') {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        value={lowSlope ?? undefined} 
                                                        onChange={(v) => {
                                                            setLowSlope(v === null || v === undefined ? null : Number(v));
                                                        }} 
                                                        className={`obd-roof-modal__input ${hasOneOverrideOnly && slopeOverrideToApi(lowSlope) === null ? 'obd-input-error' : ''}`} 
                                                        controls={false}
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Normal</td>
                                                <td className="obd-roof-modal__matrix-val-dim">
                                                    {calculatedSlopes.normalSum}
                                                </td>
                                                <td>
                                                    <InputNumber 
                                                        min={0}
                                                        disabled={designInfoAccessView}
                                                        onKeyDown={(e) => {
                                                            if (e.key === '.' || e.key === ',') {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        value={normalSlope ?? undefined} 
                                                        onChange={(v) => {
                                                            setNormalSlope(v === null || v === undefined ? null : Number(v));
                                                        }} 
                                                        className={`obd-roof-modal__input ${hasOneOverrideOnly && slopeOverrideToApi(normalSlope) === null ? 'obd-input-error' : ''}`} 
                                                        controls={false}
                                                    />
                                                </td>
                                            </tr>
                                            <tr className="obd-roof-modal__matrix-total-row">
                                                <td>Total</td>
                                                <td className="obd-roof-modal__matrix-val-dim">
                                                    {calculatedSlopes.totalSum}
                                                </td>
                                                <td className="obd-roof-modal__matrix-total-val">
                                                    {Math.trunc(lowSlope ?? 0) + Math.trunc(normalSlope ?? 0)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {hasOneOverrideOnly && (
                                    <div style={{ padding: '10px 12px 0' }}>
                                        <div className="invalid-feedbacks" style={{ display: 'block' }}>
                                            Missing value in override column. Pelase fill in both values to save changes.
                                        </div>
                                    </div>
                                )}
                            </div>

                            {(systemDesign?.OpportunityInterest?.RRR || opportunityDetails?.OpportunityInterest?.RRR) && (
                                <div className="obd-roof-modal__config-card">
                                    <div className="obd-roof-modal__card-header">
                                        <h3 className="obd-roof-modal__section-title">R&R design</h3>
                                    </div>
                                    <div className="obd-roof-modal__field" style={{ padding: '12px' }}>
                                        <label className="obd-roof-modal__matrix-val-dim" style={{ display: 'block', marginBottom: '8px' }}>
                                            R&R system size in Kilowatts<span className="text-danger">*</span> :
                                        </label>
                                        <InputNumber 
                                            min={0} 
                                            max={100} 
                                            value={rrSystemSize} 
                                            onChange={(v) => setRrSystemSize(v === null ? null : Number(v))} 
                                            className={`obd-roof-modal__input ${submitted && (rrSystemSize === null || rrSystemSize === 0 || String(rrSystemSize) === '') ? 'obd-input-error' : ''}`} 
                                            controls={false}
                                            placeholder="Enter kW"
                                            disabled={isRRSystemSizeDisabled}
                                            maxLength={8}
                                        />
                                        {submitted && (rrSystemSize === null || rrSystemSize === 0 || String(rrSystemSize) === '') && (
                                            <div className="invalid-feedbacks" style={{ display: 'block', marginTop: '4px' }}>
                                                R&R system size is required
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </aside>

                        {/* --- Main studio (Right) --- */}
                        <main className="obd-roof-modal__main-content">
                            <div
                                className={`obd-roof-modal__main-top${Number(pricePerSqFt) <= 0 ? ' obd-roof-modal__main-top--alert' : ''}`}
                            >
                                {Number(pricePerSqFt) <= 0 ? (
                                    <div className="obd-roof-modal__studio-alert">
                                        <Warning size={16} weight="fill" />
                                        <span>Price per sq ft must be greater than 0</span>
                                    </div>
                                ) : null}
                                <div className="obd-roof-modal__studio-toolbar">
                                    <div className="obd-roof-modal__studio-toolbar-group obd-roof-modal__studio-toolbar-group--pricing">
                                        <div className="obd-roof-modal__studio-toolbar-heading">
                                            <span className="obd-roof-modal__studio-toolbar-title">Pattern &amp; pricing</span>
                                            <span className="obd-roof-modal__studio-toolbar-meta">
                                                {shingleCategory === 'cambridge' ? 'IKO Cambridge' : 'IKO Dynasty'}
                                            </span>
                                        </div>
                                        <label className="obd-roof-modal__studio-price-field" htmlFor="price-per-sq-ft">
                                            <span className="obd-roof-modal__studio-price-label">Price / sq ft</span>
                                            <CurrencyInput
                                                id="price-per-sq-ft"
                                                name="price-per-sq-ft"
                                                placeholder="$ 0.00"
                                                intlConfig={{ locale: 'en-US', currency: 'USD' }}
                                                prefix="$ "
                                                value={pricePerSqFtInput}
                                                onValueChange={(value, _name, values) => {
                                                    setPricePerSqFtInput(value ?? '');
                                                    const rawValue = values?.float ?? 0;
                                                    const clampedValue = Math.min(rawValue, 1000000);
                                                    setPricePerSqFt(clampedValue);
                                                }}
                                                onBlur={() => {
                                                    setPricePerSqFtInput(pricePerSqFt.toFixed(2));
                                                    updateMinandProposedRate(pricePerSqFt);
                                                }}
                                                decimalsLimit={2}
                                                decimalSeparator="."
                                                groupSeparator=","
                                                allowDecimals={true}
                                                allowNegativeValue={false}
                                                disabled={pricePerSqFtDisabled || designInfoAccessView}
                                                className={`ant-input obd-roof-modal__input obd-roof-modal__input--price obd-roof-modal__studio-price-input ${Number(pricePerSqFt) <= 0 && 'validation-error'}`}
                                            />
                                        </label>
                                    </div>

                                    {showMinRateandPPR ? (
                                        <>
                                            <div className="obd-roof-modal__studio-toolbar-divider" aria-hidden />
                                            <div className="obd-roof-modal__studio-toolbar-group obd-roof-modal__studio-toolbar-group--rates">
                                                <span className="obd-roof-modal__studio-toolbar-title obd-roof-modal__studio-toolbar-title--solo">
                                                    Solar rates
                                                </span>
                                                <div className="obd-roof-modal__studio-stat-pills">
                                                    <div className="obd-roof-modal__studio-stat-pill">
                                                        <span className="obd-roof-modal__studio-stat-label">Min</span>
                                                        <span className="obd-roof-modal__studio-stat-value">
                                                            {loading.rates ? (
                                                                <Spin size="small" />
                                                            ) : minSolarRate === 0 ? (
                                                                '$0.00'
                                                            ) : (
                                                                `$${minSolarRate.toFixed(3)}`
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="obd-roof-modal__studio-stat-pill obd-roof-modal__studio-stat-pill--accent">
                                                        <span className="obd-roof-modal__studio-stat-label">Proposed</span>
                                                        <span className="obd-roof-modal__studio-stat-value">
                                                            {loading.rates ? (
                                                                <Spin size="small" />
                                                            ) : proposedSolarRate === 0 ? (
                                                                '$0.00'
                                                            ) : (
                                                                `$${proposedSolarRate.toFixed(3)}`
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            </div>

                            <div className="obd-roof-modal__patterns-section">
                                {renderShingleGrid(shingleCategory)}
                            </div>

                            <nav className="obd-roof-modal__shingle-tabs" role="tablist" aria-label="Shingle product line">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={shingleCategory === 'cambridge'}
                                    className={`obd-roof-modal__shingle-tab ${shingleCategory === 'cambridge' ? 'obd-roof-modal__shingle-tab--active' : ''}`}
                                    onClick={() => handleCategoryChange('cambridge')}
                                    disabled={!canSelectCambridge || designInfoAccessView}
                                >
                                    <span className="obd-roof-modal__shingle-tab-label">IKO - Cambridge</span>
                                    <span className="obd-roof-modal__shingle-tab-count">
                                        {shinglesByCategory.cambridge.length} styles
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={shingleCategory === 'dynasty'}
                                    className={`obd-roof-modal__shingle-tab ${shingleCategory === 'dynasty' ? 'obd-roof-modal__shingle-tab--active' : ''}`}
                                    onClick={() => handleCategoryChange('dynasty')}
                                    disabled={designInfoAccessView}
                                >
                                    <span className="obd-roof-modal__shingle-tab-label">IKO - Dynasty</span>
                                    <span className="obd-roof-modal__shingle-tab-count">
                                        {shinglesByCategory.dynasty.length} styles
                                    </span>
                                </button>
                            </nav>
                        </main>
                    </div>
                </div>
            </div>

            <RoofProposalDetailModal 
                open={roofProposalOpen} 
                onClose={() => setRoofProposalOpen(false)} 
            />
        </>
    );

    if (embedded) {
        return (
            <>
                <div className="obd-roof-details-embedded obd-roof-details-embedded--studio" style={{ position: 'relative' }}>
                    {roofModalBody}
                    {(loading.saving || loading.rates) && (
                        <div
                            className={`obd-roof-details-embedded__loading obd-theme-${theme === 'dark' ? 'dark' : 'light'}`}
                            aria-busy="true"
                            aria-live="polite"
                        >
                            <Spin tip="Loading details..." />
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title="Roof details"
            footer={roofModalFooter}
            width={1280}
            theme={theme}
            className={`obd-roof-details-modal obd-theme-${theme}`}
            rootClassName={`obd-roof-details-modal-root obd-theme-${theme}`}
            maskClosable={false}
            destroyOnClose
            centered
            loading={loading.saving || loading.rates}
            loadingMessage="Loading details..."
        >
            {roofModalBody}
        </DashboardModal>
    );
};

export default RoofDetailsModal;
